import { useRef, useState } from 'react';
import { getApiBase, getToken } from '../api/httpClient';
import { useManualScreenLoading } from '../lib/loadingOverlay';
import { toast } from '../lib/toast';
import { Icon } from './Icon';
import { useListPreview, type UseListPreviewOptions } from './ListPreview';
import type { OrderAttachment } from '../types';
import { useUserPermissions, useInvalidateApp } from '../hooks';
import { confirmAction } from '../lib/confirmAction';

type UploadTarget = 'attachment' | 'design' | 'qc' | 'dispatch' | 'installation';

export function fileDownloadUrl(orderId: string, fileId: string): string {
  return `${getApiBase()}/files/${orderId}/${fileId}`;
}

export async function downloadOrderFile(orderId: string, fileId: string, name: string): Promise<void> {
  const token = getToken();
  const res = await fetch(fileDownloadUrl(orderId, fileId), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export async function deleteOrderFile(orderId: string, fileId: string): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${getApiBase()}/orders/${orderId}/files/${fileId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) {
    const body = await res.text();
    let message = body;
    try {
      message = (JSON.parse(body) as { error?: { message?: string } }).error?.message ?? body;
    } catch {}
    throw new Error(message || 'Delete failed');
  }
}

export async function uploadOrderFile(
  orderId: string,
  file: File,
  target: UploadTarget,
  options?: { lineItemId?: string; department?: string },
): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const presignRes = await fetch(`${getApiBase()}/orders/${orderId}/files/presign`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      target,
      size: file.size,
    }),
  });
  if (!presignRes.ok) {
    const body = await presignRes.text();
    let message = body;
    try {
      message = (JSON.parse(body) as { error?: { message?: string } }).error?.message ?? body;
    } catch {}
    throw new Error(message || 'Could not get upload URL');
  }
  const { fileId, uploadUrl } = (await presignRes.json()) as { fileId: string; uploadUrl: string };

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putRes.ok) throw new Error('Upload to storage failed');

  const confirmRes = await fetch(`${getApiBase()}/orders/${orderId}/files/confirm`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      fileId,
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      target,
      size: file.size,
      lineItemId: options?.lineItemId,
      department: options?.department,
    }),
  });
  if (!confirmRes.ok) {
    const body = await confirmRes.text();
    let message = body;
    try {
      message = (JSON.parse(body) as { error?: { message?: string } }).error?.message ?? body;
    } catch {}
    throw new Error(message || 'Upload confirmation failed');
  }
}

function fileListModalOptions(title: string): UseListPreviewOptions<OrderAttachment> {
  return {
    title,
    searchPlaceholder: 'Search files…',
    getSearchFields: (f) => [f.name],
  };
}

const DOWNLOAD_ICON = ['M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'];
const TRASH_ICON = ['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6', 'M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2'];

export function FileList({
  orderId,
  files,
  listPreview,
  modalTitle = 'All Files',
}: {
  orderId: string;
  files: OrderAttachment[];
  listPreview?: ReturnType<typeof useListPreview<OrderAttachment>>;
  modalTitle?: string;
}) {
  const { user, isAdmin } = useUserPermissions();
  const invalidate = useInvalidateApp();

  const canDelete = (f: OrderAttachment) => isAdmin || (user && f.uploadedBy === user.name);

  const handleDelete = async (fileId: string, name: string) => {
    const ok = await confirmAction({
      title: 'Delete file?',
      content: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      okText: 'Delete',
      danger: true,
    });
    if (!ok) return;

    try {
      await deleteOrderFile(orderId, fileId);
      toast.success('File deleted');
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const internal = useListPreview(files, {
    ...fileListModalOptions(modalTitle),
    renderModalContent: (list) => (
      <ul className="file-list">
        {list.map((f) => (
          <li key={f.id} className="file-list-item">
            <span>
              {f.name}
              {f.version > 1 && <span className="file-list-version">v{f.version}</span>}
            </span>
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  void downloadOrderFile(orderId, f.id, f.name).catch(() => toast.error('Download failed'))
                }
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                aria-label="Download"
              >
                <Icon d={DOWNLOAD_ICON} size={13} />
                <span className="btn-label-desktop">Download</span>
              </button>
              {canDelete(f) && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm text-danger"
                  onClick={() => void handleDelete(f.id, f.name)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                  aria-label="Delete"
                >
                  <Icon d={TRASH_ICON} size={13} />
                  <span className="btn-label-desktop">Delete</span>
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    ),
  });
  const preview = listPreview ?? internal;
  const { visible } = preview;

  if (files.length === 0) return null;

  const renderRow = (f: OrderAttachment) => (
    <li key={f.id} className="file-list-item">
      <span>
        {f.name}
        {f.version > 1 && <span className="file-list-version">v{f.version}</span>}
      </span>
      <div style={{ display: 'flex', gap: '0.35rem' }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() =>
            void downloadOrderFile(orderId, f.id, f.name).catch(() => toast.error('Download failed'))
          }
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
          aria-label="Download"
        >
          <Icon d={DOWNLOAD_ICON} size={13} />
          <span className="btn-label-desktop">Download</span>
        </button>
        {canDelete(f) && (
          <button
            type="button"
            className="btn btn-ghost btn-sm text-danger"
            onClick={() => void handleDelete(f.id, f.name)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
            aria-label="Delete"
          >
            <Icon d={TRASH_ICON} size={13} />
            <span className="btn-label-desktop">Delete</span>
          </button>
        )}
      </div>
    </li>
  );

  return (
    <>
      <ul className="file-list">{visible.map(renderRow)}</ul>
      {!listPreview && preview.viewAllLink}
      {!listPreview && preview.viewAllModal}
    </>
  );
}

export function FileUpload({
  orderId,
  target,
  label,
  icon,
  buttonClassName,
  lineItemId,
  department,
  onUploaded,
}: {
  orderId: string;
  target: UploadTarget;
  label?: string;
  icon?: string | readonly string[];
  buttonClassName?: string;
  lineItemId?: string;
  department?: string;
  onUploaded?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  useManualScreenLoading(uploading, 'Uploading file…');

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadOrderFile(orderId, file, target, { lineItemId, department });
      toast.success('File uploaded');
      onUploaded?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="file-upload-wrap">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.mp4,.mov,.ai,.cdr,.eps,.svg,.zip,.psd,.psb,.indd,.idml,.tiff,.tif,.dxf,.bmp,.docx,.xlsx"
        className="file-input-hidden"
        onChange={(e) => void handleChange(e)}
      />
      <button
        type="button"
        className={buttonClassName ?? 'btn btn-secondary file-upload-btn'}
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {icon && !uploading && <Icon d={icon} size={13} />}
        {label ?? 'Upload file'}
      </button>
    </div>
  );
}
